import { useMutation, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { USE_GET_TRAINING_TYPES_QUERY_KEY } from '@/api/trainers'

import { useInvalidateServices } from './get-services'
import { ServiceSku } from './services.types'

export interface SkuFields {
  cost: number
  duration?: number
  name: string
  productId: number
  quantity?: number
}

export interface UpdateSkuRequest extends Partial<SkuFields> {
  active?: boolean
  id: number
}

export const createSku = async (data: SkuFields) => {
  const response = await authAPI.post<ServiceSku>('/sku', data)
  return response.data
}

export const updateSku = async ({ id, ...data }: UpdateSkuRequest) => {
  const response = await authAPI.put<ServiceSku>(`/sku/${id}`, data)
  return response.data
}

export const deleteSku = async (id: number) => {
  await authAPI.delete(`/sku/${id}`)
}

// Note: every subservice owns a training type named "<service> <duration> min", created by the BE
const useInvalidateSkus = () => {
  const queryClient = useQueryClient()
  const invalidateServices = useInvalidateServices()

  return () =>
    Promise.all([
      invalidateServices(),
      queryClient.invalidateQueries({ queryKey: [USE_GET_TRAINING_TYPES_QUERY_KEY] }),
    ])
}

export const useCreateSku = () => {
  const invalidate = useInvalidateSkus()
  return useMutation({ mutationFn: createSku, onSuccess: invalidate })
}

export const useUpdateSku = () => {
  const invalidate = useInvalidateSkus()
  return useMutation({ mutationFn: updateSku, onSuccess: invalidate })
}

export const useDeleteSku = () => {
  const invalidate = useInvalidateSkus()
  return useMutation({ mutationFn: deleteSku, onSuccess: invalidate })
}
